import { c as _c } from "react/compiler-runtime";
import type { CSSProperties, ReactNode } from "react";
import { ENTRY_META_AUTHOR_LINK_CLASS } from "../theme/typographyTokens";
import { type AboutAuthorPerson, resolveAboutAuthorEyebrow, resolveAboutAuthorViewAllHref, resolveAboutAuthorViewAllLabel } from "../ui/lib/aboutAuthorBinding";
export type AboutAuthorCardConfig = {
  avatarSize?: string;
  eyebrowLabel?: string;
  linkProfile?: boolean;
  showAvatar?: boolean;
  showBio?: boolean;
  showSocials?: boolean;
  showVerifiedBadge?: boolean;
  showViewAll?: boolean;
  verifiedBadgeLabel?: string;
  viewAllLabel?: string;
  viewAllUrl?: string;
};
type AboutAuthorCardProps = {
  className?: string;
  config: AboutAuthorCardConfig;
  person: AboutAuthorPerson;
  placeholderImageSrc?: string;
  style?: CSSProperties;
};
function parsePx(value: string | undefined, fallback: number): number {
  if (!value?.trim()) {
    return fallback;
  }
  const n = Number.parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}
function isExternalHref(href: string): boolean {
  return /^https?:\/\//i.test(href);
}
function profileLink(href: string, style: CSSProperties, children: ReactNode): ReactNode {
  if (!href) {
    return <span style={style}>{children}</span>;
  }
  const external = isExternalHref(href);
  return <a className={ENTRY_META_AUTHOR_LINK_CLASS} href={href} rel={external ? "noopener noreferrer" : undefined} style={{
    ...style,
    color: "inherit",
    textDecoration: "none"
  }} target={external ? "_blank" : undefined}>
			{children}
		</a>;
}
function CheckIcon(t0) {
  const $ = _c(4);
  const {
    size: t1
  } = t0;
  const size = t1 === undefined ? 12 : t1;
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <title>Verified</title>;
    t3 = <path d="M20 6 9 17l-5-5" />;
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  let t4;
  if ($[2] !== size) {
    t4 = <svg aria-hidden={true} fill="none" height={size} stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.4} viewBox="0 0 24 24" width={size}>{t2}{t3}</svg>;
    $[2] = size;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  return t4;
}
function MailIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg aria-hidden={true} fill="none" height={16} stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" width={16}><title>Email</title><rect height="14" rx="2" width="18" x="3" y="5" /><path d="m3 7 9 6 9-6" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function ArticlesIcon() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <svg aria-hidden={true} fill="none" height={16} stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24" width={16}><title>Articles</title><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" /><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" /></svg>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/** Public / canvas about-the-author card. Empty slots are omitted. */
export function AboutAuthorCard(t0) {
  const $ = _c(70);
  const {
    className,
    config,
    person,
    placeholderImageSrc,
    style
  } = t0;
  let t1;
  if ($[0] !== config.eyebrowLabel) {
    t1 = resolveAboutAuthorEyebrow(config.eyebrowLabel);
    $[0] = config.eyebrowLabel;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const eyebrow = t1;
  const showAvatar = config.showAvatar !== false;
  const showVerified = config.showVerifiedBadge !== false && person.isVerified;
  let t2;
  if ($[2] !== config.showBio || $[3] !== person.bio) {
    t2 = config.showBio !== false && Boolean(person.bio?.trim());
    $[2] = config.showBio;
    $[3] = person.bio;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const showBio = t2;
  const showSocials = config.showSocials !== false;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[5] !== className || $[6] !== config.avatarSize || $[7] !== config.linkProfile || $[8] !== config.showViewAll || $[9] !== config.verifiedBadgeLabel || $[10] !== config.viewAllLabel || $[11] !== config.viewAllUrl || $[12] !== eyebrow || $[13] !== person || $[14] !== placeholderImageSrc || $[15] !== showAvatar || $[16] !== showBio || $[17] !== showSocials || $[18] !== showVerified || $[19] !== style) {
    const viewAllHref = config.showViewAll === false ? "" : resolveAboutAuthorViewAllHref(person, config.viewAllUrl);
    let t10;
    if ($[27] !== config.viewAllLabel) {
      t10 = resolveAboutAuthorViewAllLabel(config.viewAllLabel);
      $[27] = config.viewAllLabel;
      $[28] = t10;
    } else {
      t10 = $[28];
    }
    const viewAllLabel = t10;
    let t11;
    if ($[29] !== config.avatarSize) {
      t11 = parsePx(config.avatarSize, 72);
      $[29] = config.avatarSize;
      $[30] = t11;
    } else {
      t11 = $[30];
    }
    const avatarPx = t11;
    const profileHref = config.linkProfile === false ? "" : person.profileUrl?.trim() ?? "";
    let t12;
    if ($[31] !== person.imageUrl || $[32] !== placeholderImageSrc) {
      t12 = person.imageUrl?.trim() || placeholderImageSrc || "";
      $[31] = person.imageUrl;
      $[32] = placeholderImageSrc;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    const imageSrc = t12;
    let t13;
    if ($[34] !== person.email || $[35] !== showSocials) {
      t13 = showSocials && person.email?.trim() ? `mailto:${person.email.trim()}` : "";
      $[34] = person.email;
      $[35] = showSocials;
      $[36] = t13;
    } else {
      t13 = $[36];
    }
    const emailHref = t13;
    let t14;
    if ($[37] !== person.socialLinks || $[38] !== showSocials) {
      t14 = showSocials ? person.socialLinks : [];
      $[37] = person.socialLinks;
      $[38] = showSocials;
      $[39] = t14;
    } else {
      t14 = $[39];
    }
    const socials = t14;
    const hasFooter = Boolean(emailHref) || socials.length > 0 || Boolean(viewAllHref && viewAllLabel);
    const nameStyle = {
      margin: 0,
      fontFamily: "var(--tt-font-sans)",
      fontSize: "1.35rem",
      fontWeight: 700,
      lineHeight: 1.25,
      color: "var(--tt-color-fg)"
    };
    let t15;
    if ($[40] !== className) {
      t15 = ["pb-about-author", className].filter(Boolean);
      $[40] = className;
      $[41] = t15;
    } else {
      t15 = $[41];
    }
    t7 = t15.join(" ");
    t8 = style;
    if ($[42] !== eyebrow) {
      t9 = eyebrow ? <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14
      }}><span aria-hidden={true} style={{
          width: 28,
          height: 3,
          borderRadius: 999,
          background: "var(--tt-color-primary)",
          flexShrink: 0
        }} /><p style={{
          margin: 0,
          fontFamily: "var(--tt-font-sans)",
          fontSize: 12,
          fontWeight: 700,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          color: "var(--tt-color-fg)"
        }}>{eyebrow}</p></div> : null;
      $[42] = eyebrow;
      $[43] = t9;
    } else {
      t9 = $[43];
    }
    let t16;
    if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        borderRadius: 16,
        border: "1px solid color-mix(in srgb, var(--tt-color-muted) 28%, transparent)",
        background: "var(--tt-color-bg)",
        boxShadow: "0 8px 28px color-mix(in srgb, var(--tt-color-fg) 6%, transparent)",
        padding: "22px 24px"
      };
      t16 = {
        display: "flex",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap"
      };
      $[44] = t16;
      $[45] = t3;
    } else {
      t16 = $[44];
      t3 = $[45];
    }
    const t17 = showAvatar && imageSrc ? <div style={{
      position: "relative",
      width: avatarPx,
      height: avatarPx
    }}>{profileLink(profileHref, {
        display: "block",
        lineHeight: 0
      }, <img alt="" height={avatarPx} src={imageSrc} style={{
        width: avatarPx,
        height: avatarPx,
        borderRadius: "50%",
        objectFit: "cover",
        display: "block"
      }} width={avatarPx} />)}{showVerified ? <span aria-hidden={true} style={{
        position: "absolute",
        right: 0,
        bottom: 0,
        width: 22,
        height: 22,
        borderRadius: "50%",
        background: "var(--tt-color-primary)",
        color: "var(--tt-color-bg)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        border: "2px solid var(--tt-color-bg)"
      }}><CheckIcon size={11} /></span> : null}</div> : null;
    let t18;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = {
        minWidth: 0,
        flex: "1 1 180px"
      };
      $[46] = t18;
    } else {
      t18 = $[46];
    }
    const t19 = profileLink(profileHref, nameStyle, person.name);
    let t20;
    if ($[47] !== config.verifiedBadgeLabel || $[48] !== person.title || $[49] !== showVerified) {
      t20 = showVerified ? <span style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        marginTop: 8,
        padding: "3px 10px",
        borderRadius: 999,
        fontFamily: "var(--tt-font-sans)",
        fontSize: 12,
        fontWeight: 600,
        color: "var(--tt-color-primary)",
        background: "color-mix(in srgb, var(--tt-color-primary) 12%, transparent)"
      }}><CheckIcon size={12} />{config.verifiedBadgeLabel?.trim() || "Verified author"}</span> : person.title?.trim() ? <p style={{
        margin: "6px 0 0",
        fontSize: 13,
        color: "var(--tt-color-muted)",
        fontFamily: "var(--tt-font-sans)"
      }}>{person.title.trim()}</p> : null;
      $[47] = config.verifiedBadgeLabel;
      $[48] = person.title;
      $[49] = showVerified;
      $[50] = t20;
    } else {
      t20 = $[50];
    }
    let t21;
    if ($[51] !== t19 || $[52] !== t20) {
      t21 = <div style={t18}>{t19}{t20}</div>;
      $[51] = t19;
      $[52] = t20;
      $[53] = t21;
    } else {
      t21 = $[53];
    }
    if ($[54] !== t17 || $[55] !== t21) {
      t4 = <div style={t16}>{t17}{t21}</div>;
      $[54] = t17;
      $[55] = t21;
      $[56] = t4;
    } else {
      t4 = $[56];
    }
    if ($[57] !== person.bio || $[58] !== showBio) {
      t5 = showBio ? <p style={{
        margin: "16px 0 0",
        fontFamily: "var(--tt-font-sans)",
        fontSize: 15,
        lineHeight: 1.65,
        color: "var(--tt-color-fg)"
      }}>{person.bio}</p> : null;
      $[57] = person.bio;
      $[58] = showBio;
      $[59] = t5;
    } else {
      t5 = $[59];
    }
    t6 = hasFooter ? <div style={{
      marginTop: 18,
      paddingTop: 14,
      borderTop: "1px solid color-mix(in srgb, var(--tt-color-muted) 28%, transparent)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap"
    }}><div style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10
      }}>{emailHref ? <a aria-label="Email author" href={emailHref} style={{
          color: "var(--tt-color-muted)",
          display: "inline-flex"
        }}><MailIcon /></a> : null}{socials.map(_temp)}</div>{viewAllHref && viewAllLabel ? <a href={viewAllHref} rel={isExternalHref(viewAllHref) ? "noopener noreferrer" : undefined} style={{
        fontFamily: "var(--tt-font-sans)",
        fontSize: 14,
        fontWeight: 600,
        color: "var(--tt-color-primary)",
        textDecoration: "none"
      }} target={isExternalHref(viewAllHref) ? "_blank" : undefined}>{viewAllLabel} →</a> : null}</div> : null;
    $[5] = className;
    $[6] = config.avatarSize;
    $[7] = config.linkProfile;
    $[8] = config.showViewAll;
    $[9] = config.verifiedBadgeLabel;
    $[10] = config.viewAllLabel;
    $[11] = config.viewAllUrl;
    $[12] = eyebrow;
    $[13] = person;
    $[14] = placeholderImageSrc;
    $[15] = showAvatar;
    $[16] = showBio;
    $[17] = showSocials;
    $[18] = showVerified;
    $[19] = style;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t3 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t10;
  if ($[60] !== t3 || $[61] !== t4 || $[62] !== t5 || $[63] !== t6) {
    t10 = <div style={t3}>{t4}{t5}{t6}</div>;
    $[60] = t3;
    $[61] = t4;
    $[62] = t5;
    $[63] = t6;
    $[64] = t10;
  } else {
    t10 = $[64];
  }
  let t11;
  if ($[65] !== t10 || $[66] !== t7 || $[67] !== t8 || $[68] !== t9) {
    t11 = <section className={t7} style={t8}>{t9}{t10}</section>;
    $[65] = t10;
    $[66] = t7;
    $[67] = t8;
    $[68] = t9;
    $[69] = t11;
  } else {
    t11 = $[69];
  }
  return t11;
}
function _temp(link) {
  return <a aria-label={link.network} href={link.url} key={`${link.network}-${link.url}`} rel="noopener noreferrer" style={{
    color: "var(--tt-color-muted)",
    display: "inline-flex"
  }} target="_blank"><ArticlesIcon /></a>;
}